import { useEffect, useRef, useState } from "react";

interface Props {
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_HEIGHT_PX = 220;

export function MessageInput({ onSend, disabled, placeholder }: Props) {
  const [value, setValue] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow up to MAX_HEIGHT_PX, then scroll inside the textarea.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [value]);

  const trySend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
    // Reset height immediately so the well snaps back.
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (ta) ta.style.height = "auto";
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd+Enter (macOS) or Ctrl+Enter (other) submits. Plain Enter inserts a
    // newline so the composer behaves like a normal text editor.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      trySend();
    }
  };

  return (
    <form
      className="coach__composer"
      onSubmit={(e) => {
        e.preventDefault();
        trySend();
      }}
    >
      <div className="coach__composer-well">
        <textarea
          ref={taRef}
          className="coach__composer-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={
            placeholder ??
            (disabled
              ? "Coach is replying…"
              : "Write to the coach. Mention a phase code (S2, DR1, C5) for a precise answer.")
          }
          disabled={disabled}
          aria-label="Message to the coach"
        />
        <div className="coach__composer-actions">
          <span className="coach__composer-hint">
            <kbd>⌘</kbd>
            <kbd>⏎</kbd>
            <span>to send</span>
          </span>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={disabled || value.trim().length === 0}
          >
            Send
          </button>
        </div>
      </div>
    </form>
  );
}
